// Admin dashboard — pure utilities, constants and text-builders.
// Everything in this file is intentionally free of React state; it lives
// beside AdminDashboard.jsx purely to keep that component's file size
// manageable. No behavior is defined here that wasn't previously inline
// in AdminDashboard.jsx — this file is a straight structural extraction
// (see the P3 refactor task, Jan 2026).
//
// If you find yourself adding React hooks/JSX/state here, put it back in
// AdminDashboard.jsx or the sibling `components.jsx`.

import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import {
  Truck, CalendarDays, AlertCircle, Store, PackageCheck,
} from "lucide-react";

// ---------------------------- Formatters -----------------------------------

// Human-friendly "time ago" formatter used by the Active Sessions modal.
// Accepts an ISO 8601 string; returns "" when the value is missing/invalid.
export function formatRelativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diffMs = Date.now() - then;
  const s = Math.round(diffMs / 1000);
  if (s < 60) return "Just now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m} minute${m === 1 ? "" : "s"} ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? "" : "s"} ago`;
  const d = Math.round(h / 24);
  if (d === 1) return "Yesterday";
  if (d < 7) return `${d} days ago`;
  const w = Math.round(d / 7);
  if (w < 5) return `${w} week${w === 1 ? "" : "s"} ago`;
  const mo = Math.round(d / 30);
  return `${mo} month${mo === 1 ? "" : "s"} ago`;
}

// Format a BRIDAL pre-registration's Home Delivery charge in Indian
// currency. Reads `home_delivery_amount_paise` (staff-entered per invoice)
// and falls back to 30000p (₹300) ONLY for legacy rows that were
// chargeable BEFORE the custom-amount feature — the backend applies the
// same fallback so the two agree. `row` is any object shaped like a
// submission doc.
export function rowChargeRupees(row) {
  const p = Number(row?.home_delivery_amount_paise) || 0;
  return Math.round((p > 0 ? p : 30000) / 100);
}
export function rowChargeLabel(row) {
  return `₹${rowChargeRupees(row).toLocaleString("en-IN")}`;
}

// ---------------------------- Enum-style constants -------------------------

export const STATUS_COLORS = {
  Pending: "bg-[#F2E9DA] text-[#7A5A22]",
  "Ready for Dispatch": "bg-[#F0E3D6] text-[#7A3A15]",
  Delivered: "bg-[#D6E9D5] text-[#2D4A22]",
};

export const STATUSES = ["Pending", "Ready for Dispatch", "Delivered"];

/**
 * Format a server-generated ISO timestamp for the mobile status-change
 * line (e.g. "24 Sep 2026, 7:18 PM"). Always renders in India Standard
 * Time (Asia/Kolkata) regardless of the staff device's clock.  Uses
 * Intl.DateTimeFormat for correct DST-free IST offset (+5:30).  Returns
 * an empty string on any parse error so callers can chain a simple
 * truthiness check.
 *
 * Feb-2026 — introduced with the status-change timestamp feature so the
 * "Delivered • …" and "Ready since • …" pills sourced from server-side
 * `delivered_at` / `ready_at` always agree with India business time.
 */
export function formatIstStatusTs(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const date = new Intl.DateTimeFormat("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "numeric", month: "short", year: "numeric",
    }).format(d);
    const time = new Intl.DateTimeFormat("en-IN", {
      timeZone: "Asia/Kolkata",
      hour: "numeric", minute: "2-digit", hour12: true,
    }).format(d).replace(/\s?am/i, " AM").replace(/\s?pm/i, " PM").trim();
    return `${date}, ${time}`;
  } catch { return ""; }
}

/**
 * Return the status-change label + timestamp to show on the mobile
 * card for a row, or an empty string if none is appropriate for the
 * row's current status. Only the timestamp matching the CURRENT status
 * is surfaced — a stale `delivered_at` from a subsequently-reverted
 * row is intentionally hidden (though it stays in the database for
 * audit).
 */
export function statusChangeLine(r) {
  if (!r) return "";
  if (r.status === "Delivered" && r.delivered_at) {
    const t = formatIstStatusTs(r.delivered_at);
    return t ? `Delivered • ${t}` : "";
  }
  if (r.status === "Ready for Dispatch" && r.ready_at) {
    const t = formatIstStatusTs(r.ready_at);
    return t ? `Ready since • ${t}` : "";
  }
  return "";
}

// Sort options — grouped for the Sort popover. `value` is the internal sort key.
export const SORT_GROUPS = [
  { group: "Invoice Number", options: [
    { value: "invoice_asc",     label: "Low → High" },
    { value: "invoice_desc",    label: "High → Low" },
  ]},
  { group: "Delivery Date", options: [
    { value: "delivery_asc",    label: "Earliest → Latest" },
    { value: "delivery_desc",   label: "Latest → Earliest" },
  ]},
  { group: "Pickup Date", options: [
    { value: "pickup_asc",      label: "Earliest → Latest" },
    { value: "pickup_desc",     label: "Latest → Earliest" },
  ]},
  { group: "Submitted", options: [
    { value: "submitted_desc",  label: "Newest → Oldest" },
    { value: "submitted_asc",   label: "Oldest → Newest" },
  ]},
  { group: "Customer Name", options: [
    { value: "name_asc",        label: "A → Z" },
    { value: "name_desc",       label: "Z → A" },
  ]},
  { group: "Status", options: [
    { value: "status_asc",      label: "Pending → Ready → Delivered" },
    { value: "status_desc",     label: "Delivered → Ready → Pending" },
  ]},
  { group: "Fulfilment", options: [
    { value: "fulfillment_asc", label: "Delivery first" },
    { value: "fulfillment_desc",label: "Pickup first" },
  ]},
];

export const SORT_LABELS = {
  invoice_asc:      "Invoice · Low → High",
  invoice_desc:     "Invoice · High → Low",
  delivery_asc:     "Delivery Date · Earliest → Latest",
  delivery_desc:    "Delivery Date · Latest → Earliest",
  pickup_asc:       "Pickup Date · Earliest → Latest",
  pickup_desc:      "Pickup Date · Latest → Earliest",
  submitted_desc:   "Submitted · Newest → Oldest",
  submitted_asc:    "Submitted · Oldest → Newest",
  name_asc:         "Name · A → Z",
  name_desc:        "Name · Z → A",
  status_asc:       "Status · Pending → Delivered",
  status_desc:      "Status · Delivered → Pending",
  fulfillment_asc:  "Fulfilment · Delivery first",
  fulfillment_desc: "Fulfilment · Pickup first",
};

export const STATUS_RANK = { "Pending": 0, "Ready for Dispatch": 1, "Delivered": 2 };

// ---------------------------- Saved Filter Views ---------------------------
// One-tap chips that snap the dashboard to the filter combos staff hit
// dozens of times a day. Each preset lists the EXACT filter state it
// implies — chips highlight as "active" only when every listed field
// matches, so opening the Filter drawer and tweaking anything cleanly
// deselects the chip. All presets assume view === "active"; the chip
// row is not rendered on the Delivered / Recycle Bin tabs.
//
// `preset` is a partial filter-state; fields NOT listed here are cleared
// to their default when the chip is applied (`applyView` in the
// component handles the reset). This keeps a two-day-old "today" chip
// from silently including yesterday's status filter.
export const todayIso = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export const SAVED_VIEWS = [
  {
    key: "today_deliveries",
    label: "Today's Deliveries",
    icon: Truck,
    testId: "saved-view-today-deliveries",
    preset: () => ({ deliveryFilter: "today", fulfillment: "home_delivery" }),
  },
  {
    key: "tomorrow_deliveries",
    label: "Tomorrow's Deliveries",
    icon: Truck,
    testId: "saved-view-tomorrow-deliveries",
    preset: () => ({ deliveryFilter: "tomorrow", fulfillment: "home_delivery" }),
  },
  {
    key: "week_deliveries",
    label: "This Week's Deliveries",
    icon: CalendarDays,
    testId: "saved-view-week-deliveries",
    preset: () => ({ deliveryFilter: "week", fulfillment: "home_delivery" }),
  },
  {
    key: "unassigned",
    label: "Unassigned Delivery Date",
    icon: AlertCircle,
    testId: "saved-view-unassigned",
    preset: () => ({ deliveryFilter: "unassigned", fulfillment: "home_delivery" }),
  },
  {
    key: "today_pickups",
    label: "Today's Pickups",
    icon: Store,
    testId: "saved-view-today-pickups",
    preset: () => ({ fulfillment: "store_pickup", pickupDateFilter: todayIso() }),
  },
  {
    key: "ready_dispatch",
    label: "Ready for Dispatch",
    icon: PackageCheck,
    testId: "saved-view-ready",
    preset: () => ({ status: "Ready for Dispatch" }),
  },
  {
    key: "pending",
    label: "Pending",
    icon: AlertCircle,
    testId: "saved-view-pending",
    preset: () => ({ status: "Pending" }),
  },
];

// ---------------------------- Invoice share / WhatsApp ---------------------

// Human-readable pickup slot map (mirrors backend PICKUP_SLOTS). Kept
// here as a plain const so the share formatter can convert "15:00-16:00"
// into "3:00 PM – 4:00 PM" without waiting on a network round-trip.
export const SHARE_SLOT_LABELS = {
  "12:00-13:00": "12:00 PM – 1:00 PM",
  "13:00-14:00": "1:00 PM – 2:00 PM",
  "14:00-15:00": "2:00 PM – 3:00 PM",
  "15:00-16:00": "3:00 PM – 4:00 PM",
  "16:00-17:00": "4:00 PM – 5:00 PM",
  "17:00-18:00": "5:00 PM – 6:00 PM",
  "18:00-19:00": "6:00 PM – 7:00 PM",
  "19:00-20:00": "7:00 PM – 8:00 PM",
};

/**
 * Build the plain-text WhatsApp-friendly share body for one invoice.
 * Strict rules (per product spec):
 *   • Fields appear in the exact requested order.
 *   • NO submission id, submitted timestamp, payment/order id, or
 *     internal DB fields.
 *   • Data ONLY from the given `r` row — never from linked companions.
 *   • Missing optional fields display "None" (never blank).
 *   • Missing Maps URL displays "Not provided".
 */
export function buildInvoiceShareText(r, notes = r?.internal_notes) {
  const lines = [];
  const fulfilment = r.fulfillment_type === "store_pickup" ? "In-Store Pickup" : "Home Delivery";

  // Address block — for home delivery, full customer-entered address;
  // for in-store pickup, the customer only entered a date + slot, so
  // that becomes the "address" line.
  let addressBlock;
  let mapsLink;
  if (r.fulfillment_type === "store_pickup") {
    const dt = r.pickup_date ? (() => {
      try { return format(parseISO(r.pickup_date), "d MMMM yyyy"); } catch { return r.pickup_date; }
    })() : "";
    const slot = SHARE_SLOT_LABELS[r.pickup_time_slot] || r.pickup_time_slot || "";
    const parts = [];
    if (dt) parts.push(dt);
    if (slot) parts.push(slot);
    addressBlock = "In-Store Pickup" + (parts.length ? " — " + parts.join(" · ") : "");
    mapsLink = "";
  } else {
    const addrLines = [];
    if (r.flat_wing_floor) addrLines.push(r.flat_wing_floor);
    if (r.building_society) addrLines.push(r.building_society);
    const streetLine =
      r.address?.formatted_address ||
      [r.address?.street, r.address?.city, r.address?.state, r.address?.pincode]
        .filter(Boolean).join(", ");
    if (streetLine) addrLines.push(streetLine);
    if (r.landmark) addrLines.push(`Landmark: ${r.landmark}`);
    addressBlock = addrLines.join("\n") || "Not provided";
    mapsLink = r.address?.maps_url || "";
  }

  const dupattaLabel =
    r.dupatta_status === "collected" ? "Collected"
    : r.dupatta_status === "include" ? "Pending"
    : r.fulfillment_type === "store_pickup" ? "N/A (In-Store Pickup)"
    : "Pending";

  // Conditional optional fields — treat null/empty/whitespace-only as blank.
  // If blank, the line is omitted entirely (no "None"/"N/A" placeholder).
  const rawCust = (r.additional_customisation === "yes"
    ? (r.customisation_details || "")
    : ""
  ).trim();
  const rawNote = (r.special_instructions || "").trim();

  // Mobile: strip anything except digits then take last 10 to keep the
  // stored value byte-identical (never alter the customer's digits).
  const digits = String(r.mobile || "").replace(/\D/g, "").slice(-10);
  const mobileFmt = digits ? `+91 ${digits}` : `+91 ${r.mobile || ""}`.trim();

  // Group B (Bridal) may carry a salesman assignment — surface it right
  // below the invoice line, with exactly one blank line before the
  // customer info block. Sider invoices never get this line.
  const invoiceGroup = /^1\d{3}$/.test(String(r.invoice_number || "")) ? "A" : "B";
  const salesmanName = (r.salesman || "").trim();
  const includeSalesman = invoiceGroup === "B" && !!salesmanName;

  // Visual dupatta indicator — ✅ collected, ❎ pending. Only appended for
  // the two known statuses so "N/A (In-Store Pickup)" stays clean.
  const dupattaWithEmoji =
    dupattaLabel === "Collected" ? "Collected ✅"
    : dupattaLabel === "Pending"   ? "Pending ❌"
    : dupattaLabel;

  lines.push(`*Invoice Number: ${r.invoice_number}*`);
  if (includeSalesman) {
    lines.push(`*Salesman : ${salesmanName}*`);
    lines.push("");
  }
  lines.push(`Customer Name: ${r.customer_name}`);
  lines.push(`Mobile Number: ${mobileFmt}`);
  lines.push(`Fulfilment: ${fulfilment}`);
  lines.push(`Address: ${addressBlock}`);
  lines.push("");
  lines.push("Google Maps Location:");
  lines.push(mapsLink || "Not provided");
  lines.push("");
  lines.push(`Dupatta Status: ${dupattaWithEmoji}`);
  if (rawCust) lines.push(`Additional Customisation: ${rawCust}`);
  if (rawNote) lines.push(`Special Note: ${rawNote}`);
  // Internal (staff) notes — separate collection, newest first, same order as the drawer.
  const internal = (notes || []).map((n) => String(typeof n === "string" ? n : n?.text || "").trim()).filter(Boolean);
  if (internal.length === 1) lines.push(`Internal Notes: ${internal[0]}`);
  else if (internal.length > 1) {
    lines.push("Internal Notes:");
    internal.forEach((t) => lines.push(`- ${t}`));
  }
  return lines.join("\n");
}

/**
 * Share (or clipboard-fallback) an invoice's fulfilment text. Uses the
 * native Web Share sheet on mobile so staff can pick WhatsApp/Messages
 * in one tap. Falls back to `navigator.clipboard` with a toast.
 * Internal notes come with the list row (`internal_notes`), so the share
 * stays synchronous inside the tap gesture (required by iOS Safari).
 */
export async function shareInvoice(r) {
  return shareText(buildInvoiceShareText(r), `Invoice ${r.invoice_number} · ${r.customer_name}`);
}

export const BULK_SHARE_DIVIDER = "--------------------";

/** One consolidated plain-text message: the full individual share text of every
 *  selected invoice, separated by a blank line + divider + blank line. */
export function buildBulkShareText(rows) {
  return (rows || []).map((r) => buildInvoiceShareText(r)).join(`\n\n${BULK_SHARE_DIVIDER}\n\n`);
}

export async function shareInvoicesBulk(rows) {
  const n = rows.length;
  return shareText(buildBulkShareText(rows), `${n} invoice${n === 1 ? "" : "s"} · fulfilment details`,
    `Details for ${n} invoice${n === 1 ? "" : "s"} copied — ready to share.`);
}

async function shareText(text, title, copiedMsg = "Invoice details copied — ready to share.") {
  try {
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      await navigator.share({ title, text });
      return;
    }
  } catch (err) {
    // User cancelled the native share sheet — that's not an error.
    if (err && (err.name === "AbortError" || err.code === 20)) return;
    // Fall through to clipboard fallback on any other native-share failure.
  }
  // Clipboard fallback.
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      // Very old browsers — synthetic textarea copy.
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    toast.success(copiedMsg);
  } catch {
    toast.error("Could not copy invoice details. Please try again.");
  }
}

// -------------------------- WhatsApp customer notify ------------------------
// Frontend-only feature: builds a friendly customer-facing message from the
// row already returned by /admin/submissions (group-scoped intrinsically),
// then opens `https://wa.me/91<digits>?text=...`. Works on mobile (native
// WhatsApp) and desktop (WhatsApp Web). We NEVER send anything ourselves —
// the staff member reviews the pre-filled message in WhatsApp and taps Send.

/**
 * Build the customer-facing WhatsApp message + a `canSend` gate.
 * `canSend=false` means "block opening WhatsApp" and `reason` is a human
 * string the confirmation modal shows the staff (e.g. missing delivery
 * date, invoice already delivered).
 */
export function buildCustomerWhatsAppMessage(r) {
  const invalidMobile = !/^\d{10}$/.test(String(r.mobile || "").replace(/\D/g, "").slice(-10));
  if (invalidMobile) {
    return { text: "", canSend: false, reason: "No valid mobile number is available for this invoice." };
  }
  // Already-delivered guard applies to BOTH fulfillment types — you should
  // not spam customers after fulfilment is complete.
  if (r.status === "Delivered") {
    return { text: "", canSend: false, reason: "This invoice has already been delivered." };
  }

  const greetingName = (r.customer_name || "").trim() || "Customer";
  const invNo = r.invoice_number;

  // Compute the window relative to TODAY (staff's local calendar day).
  // The stored date represents the first expected day; the window is
  // [stored, stored + 1 day]. We describe that window in words but ONLY
  // when it starts today or tomorrow — beyond that, saying "tomorrow"
  // would be misleading, so we block and ask the staff to send later.
  const dateWindow = (isoDate, kind /* "delivery" | "collection" */) => {
    if (!isoDate) return { line: "", canSend: false, reason: `This invoice does not have a ${kind === "delivery" ? "delivery" : "pickup"} date assigned yet.` };
    let stored;
    try { stored = parseISO(isoDate); } catch { return { line: "", canSend: false, reason: `This invoice's ${kind} date is invalid.` }; }
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const startOfStored = new Date(stored.getFullYear(), stored.getMonth(), stored.getDate());
    const daysUntil = Math.round((startOfStored - startOfToday) / 86400000);
    const verb = kind === "delivery" ? "delivered" : "ready for collection";
    if (daysUntil < 0) {
      return { line: "", canSend: false, reason: `The scheduled ${kind} date has already passed. Please update it before sending this notification.` };
    }
    if (daysUntil === 0) {
      return { line: `Your order will be ${verb} today or tomorrow.`, canSend: true, reason: "" };
    }
    if (daysUntil === 1) {
      return { line: `Your order will be ${verb} by tomorrow or the day after tomorrow.`, canSend: true, reason: "" };
    }
    return {
      line: "",
      canSend: false,
      reason: `This invoice's scheduled ${kind} date is ${daysUntil} days away. Send this notification when it is today or tomorrow.`,
    };
  };

  if (r.fulfillment_type === "store_pickup") {
    const win = dateWindow(r.pickup_date, "collection");
    if (!win.canSend) return { text: "", canSend: false, reason: win.reason };
    const text = [
      `Dear ${greetingName},`,
      "",
      "Greetings from TANISH TEXTILES",
      "",
      `We're pleased to inform you that your order, invoice No. ${invNo}, is ready for collection from our store.`,
      "",
      win.line,
      "",
      "Our team has taken care of the required finishing to ensure your piece is ready for collection.",
      "",
      "We look forward to welcoming you at TANISH TEXTILES.",
      "",
      "Thank you for choosing TANISH TEXTILES. We truly appreciate your trust. ❤️",
      "",
      "Warm regards,",
      "TANISH TEXTILES",
    ].join("\n");
    return { text, canSend: true, reason: "" };
  }

  // Home delivery path.
  const win = dateWindow(r.internal_delivery_date, "delivery");
  if (!win.canSend) return { text: "", canSend: false, reason: win.reason };

  const addrLines = [];
  if (r.flat_wing_floor) addrLines.push(r.flat_wing_floor);
  if (r.building_society) addrLines.push(r.building_society);
  const streetLine =
    r.address?.formatted_address ||
    [r.address?.street, r.address?.city, r.address?.state, r.address?.pincode].filter(Boolean).join(", ");
  if (streetLine) addrLines.push(streetLine);
  if (r.landmark) addrLines.push(`Landmark: ${r.landmark}`);
  const addressText = addrLines.join("\n") || "Address not provided";

  // Written address only — Google Maps link is intentionally NOT included
  // in the WhatsApp message. The Maps URL remains saved on the invoice
  // and is still visible in the admin Detail Drawer.
  const locBlock = `📍 Delivery Location: ${addressText}`;

  const text = [
    `Dear ${greetingName},`,
    "",
    "Greetings from TANISH TEXTILES",
    "",
    `We're pleased to inform you that your order, invoice No. ${invNo}, is ready for delivery.`,
    "",
    win.line,
    "",
    "Our team has taken care of the required finishing to ensure your piece is ready for delivery.",
    "",
    locBlock,
    "",
    "Our delivery team will coordinate with you before delivery.",
    "",
    "Thank you for choosing TANISH TEXTILES. We truly appreciate your trust and look forward to delivering your finished piece to you. ❤️",
    "",
    "Warm regards,",
    "TANISH TEXTILES",
  ].join("\n");
  return { text, canSend: true, reason: "" };
}

// -------------------------- Business-only WhatsApp gate ---------------------
// Every WhatsApp customer-contact action in this dashboard must be sent from
// the Tanish Textiles WhatsApp BUSINESS account, never the staff member's
// personal WhatsApp. WhatsApp / Meta does not expose a URL-scheme parameter
// to force the Business app — the actual dispatcher depends on which app is
// selected in the OS chooser (mobile) or which app WhatsApp's universal link
// is registered to (desktop).
//
// Launch mechanism:
//   * We use `https://wa.me/91<digits>?text=…` — WhatsApp's OFFICIAL
//     universal link. On iOS Safari and Android Chrome this is registered
//     as a Universal Link / App Link so the tap opens the native WhatsApp
//     app directly (with the OS chooser between WhatsApp / WhatsApp
//     Business when both are installed). It does NOT open web.whatsapp.com
//     on mobile — that only happens if WhatsApp is not installed at all,
//     which is a non-issue on a staff device.
//   * On desktop with WhatsApp Desktop installed the same link opens the
//     desktop app; without it, it opens WhatsApp Web — which routes
//     through whichever account is currently linked. The Business ack
//     modal reminds staff to keep Business linked on the office device.
//
// Business-only enforcement:
//   * A one-time acknowledgment modal (per browser via localStorage) is
//     shown before every WhatsApp action until confirmed, spelling out:
//     "Use WhatsApp Business only. Never send from personal WhatsApp."
//   * Persistent "Business only" reminder banners in both WhatsApp modals.
//   * No silent fallback to any other channel exists in code.
//
// Together these deliver the strongest guarantee the browser platform
// allows: staff never open WhatsApp without being explicitly reminded,
// the launch is always attempted as native-app-first, and no code path
// intentionally routes through the personal account.
export const WA_BIZ_ACK_KEY = "tt_wa_business_ack_v1";

export function isWaBizAcknowledged() {
  try { return typeof localStorage !== "undefined" && localStorage.getItem(WA_BIZ_ACK_KEY) === "1"; }
  catch { return false; }
}
export function markWaBizAcknowledged() {
  try { localStorage.setItem(WA_BIZ_ACK_KEY, "1"); } catch {}
}

/** Build the recipient URL. Uses WhatsApp's official `wa.me` universal
 * link so a tap opens the native WhatsApp app on iOS / Android (Business
 * chosen via the OS chooser). NEVER navigates to `web.whatsapp.com` on
 * mobile — WhatsApp Web is only used as a browser fallback when no
 * WhatsApp app is installed at all, which is not our case on staff
 * devices. */
export function whatsappDeepLink(r, text) {
  const digits = String(r.mobile || "").replace(/\D/g, "").slice(-10);
  return `https://wa.me/91${digits}?text=${encodeURIComponent(text)}`;
}

export function dupattaLabel(status) {
  if (status === "collected") return "Dupatta Collected";
  if (status === "include") return "Dupatta Pending";
  if (status === "not_included" || status === "exclude" || status === "no") return "No Dupatta";
  return status ? String(status) : "Not Specified";
}

// Same emoji mapping as the individual WhatsApp share (✅ collected, ❌ pending, none otherwise).
export function dupattaLabelWithEmoji(status) {
  const label = dupattaLabel(status);
  if (status === "collected") return `${label} ✅`;
  if (status === "include") return `${label} ❌`;
  return label;
}

/** Consolidated staff work list: invoice, salesman, dupatta only — never customer data. */
export function buildWorkListMessage(rows) {
  return rows
    .map((r) =>
      [
        `Invoice: ${r.invoice_number}`,
        `Salesman: ${r.salesman && String(r.salesman).trim() ? r.salesman : "Not Assigned"}`,
        `Dupatta: ${dupattaLabelWithEmoji(r.dupatta_status)}`,
      ].join("\n")
    )
    .join("\n\n");
}

export function whatsappShareLink(text) {
  return `https://wa.me/?text=${encodeURIComponent(text)}`;
}

/** Copy the 10-digit Indian mobile number (no +91, no spaces, no
 * hyphens) from a submission row to the clipboard. Uses the async
 * Clipboard API with a `document.execCommand('copy')` fallback for
 * older browsers / non-secure contexts. Shows a subtle toast; no
 * modal, no confirmation. */
export async function copyMobileDigits(row) {
  const digits = String(row?.mobile || "").replace(/\D/g, "").slice(-10);
  if (digits.length !== 10) {
    toast.error("No valid mobile number to copy");
    return;
  }
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(digits);
    } else {
      // Legacy fallback — off-screen textarea + execCommand.
      const ta = document.createElement("textarea");
      ta.value = digits;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.top = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    toast.success(`Copied ${digits}`, { duration: 1500 });
  } catch {
    toast.error("Could not copy — please copy manually");
  }
}

// ---------------------------- Misc labels ----------------------------------

export const NOTIF_LABELS = {
  CUSTOMER_DELIVERY_CONFIRMATION: "Customer",
  CUSTOMER_PICKUP_CONFIRMATION: "Customer",
  ADMIN_NEW_DELIVERY: "Admin",
  ADMIN_NEW_PICKUP: "Admin",
};

export const PICKUP_SLOT_LABELS = {
  "12:00-13:00": "12–1 PM",
  "13:00-14:00": "1–2 PM",
  "14:00-15:00": "2–3 PM",
  "15:00-16:00": "3–4 PM",
  "16:00-17:00": "4–5 PM",
  "17:00-18:00": "5–6 PM",
  "18:00-19:00": "6–7 PM",
  "19:00-20:00": "7–8 PM",
};
export const formatSlot = (v) => PICKUP_SLOT_LABELS[v] || v || "";

export const SALESMEN = ["Rajesh", "Faraz", "Mohit", "Kannu", "Akhtar", "Ravi", "Adam", "Vinay"];

/** Mirror of the backend `_search_rank`: invoice matches outrank name, mobile and other fields. */
export function searchRank(row, q) {
  const ql = String(q || "").trim().toLowerCase();
  if (!ql) return 0;
  const inv = String(row?.invoice_number || "").toLowerCase();
  const name = String(row?.customer_name || "").toLowerCase();
  const mobile = String(row?.mobile || "");
  if (inv === ql) return 0;
  if (inv.startsWith(ql)) return 1;
  if (inv.includes(ql)) return 2;
  if (name === ql) return 3;
  if (name.includes(ql)) return 4;
  if (mobile === ql) return 5;
  if (mobile.includes(ql)) return 6;
  return 7;
}

// -------------------------- Bulk WhatsApp helpers ---------------------------
// Extracted from AdminDashboard.jsx during the Feb-2026 refactor so both
// the parent dashboard (which needs `hasValidMobile` for row-selection
// gating and `buildBulkWhatsAppMessage` for opening WhatsApp) and the
// `admin/BulkWhatsAppModal` component can share one canonical
// implementation. Copy is 1:1 with the original inline definitions —
// change only via an explicit product request.

/**
 * Normalise the free-text "delivery update" typed by staff into the
 * grammatically correct fragment used inside the template body.
 *  - "Monday"                → "on Monday"
 *  - "Tomorrow"              → "tomorrow"    (no "on" prefix)
 *  - "Tomorrow or Tuesday"   → "tomorrow or Tuesday"
 *  - ""                      → ""            (caller shows "…")
 */
export function bulkDeliveryPhrase(update) {
  const raw = String(update || "").trim();
  if (!raw) return "";
  const first = raw.split(/\s+/, 1)[0].toLowerCase();
  const NO_PREFIX = new Set(["today", "tomorrow", "tonight"]);
  if (NO_PREFIX.has(first)) {
    // Lowercase only the very first character so a mid-sentence
    // proper noun like "Tomorrow or Tuesday" keeps "Tuesday" capitalised.
    return raw.charAt(0).toLowerCase() + raw.slice(1);
  }
  return `on ${raw}`;
}

/** First-name extractor with a "there" fallback so we never greet with
 * a blank name. Used only inside `buildBulkWhatsAppMessage`. */
export function firstName(fullName) {
  return String(fullName || "").trim().split(/\s+/)[0] || "there";
}

/**
 * Bulk WhatsApp template.
 *
 * Uses ONE fixed Tanish Textiles template. Only the customer first-name
 * and the staff-typed delivery update are substituted — nothing about
 * the message copy is dynamic per-customer, so every recipient gets the
 * same professional wording. We deliberately do NOT insert invoice
 * numbers, delivery dates, or maps links here — this is a light-touch
 * update template. The individual WhatsApp Share button retains its
 * richer message.
 */
export function buildBulkWhatsAppMessage(row, deliveryUpdate) {
  const name = firstName(row?.customer_name);
  const phrase = bulkDeliveryPhrase(deliveryUpdate);
  return (
`Dear ${name},

Your outfit is ready with us and all set for delivery!

We'll be delivering your piece to you ${phrase}.

Thank you for choosing Tanish Textiles. We hope you love your outfit as much as we loved creating it for you! ❤️

Warm regards,
Tanish Textiles`
  );
}

/** True when a row has a valid 10-digit Indian mobile (leading 6–9).
 * Used by both the bulk-WhatsApp queue and the openBulkWhatsApp gate. */
export function hasValidMobile(row) {
  return /^[6-9]\d{9}$/.test(String(row?.mobile || "").replace(/\D/g, "").slice(-10));
}
